import React, { useState, useRef, useContext, useEffect } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from "react-icons/rx";
import ModalHeading from '../../components/shared/modal-heading';
import { prependDefault } from '../../utils/structure-options-utils';
import MultipleAttachmentInput from '../../components/shared/multiple-attachemnt';
import { uploadMedia } from '../../services/upload-service';
import { AccountsContext } from '../../components/context/account-context';
import { getTireMedia, updateTire } from '../../services/tires-service';
import Loader from '../../components/shared/loader';

const EditModal = ({ selectedRow, setSelectedRow, fetchGlobalData, loadingGlobalData, setIsEditModalOpen, setData, fetchTires, brands, sizes, positions, vehicles, vendors, seasons, isVehicleTab = false }) => {
    const { accountId } = useContext(AccountsContext);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploadStatus, setUploadStatus] = useState({ message: '', type: '' });
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });
    const [loadingFields, setLoadingFields] = useState({});
    const [error, setError] = useState();
    const [isUploading, setIsUploading] = useState(false);
    const [fetchingMedia, setFetchingMedia] = useState(false);
    const fileInputRef = useRef(null);

    // Get the tire media data
    const fetchTireMedia = async () => {
        try {
            setFetchingMedia(true);
            const res = await getTireMedia(accountId, selectedRow.tireId);
            if (res.success) {
                const mediaFiles = res.data?.data || [];
                setUploadedFiles(mediaFiles.map(file => ({
                    mediaId: file.mediaId,
                    fileType: file.fileType,
                    fileName: file.fileName,
                    signedUrl: file.signedUrl,
                    signedThumbUrl: file.signedThumbUrl,
                    imagePreview: file.fileType?.startsWith('image/') ? file.signedThumbUrl || file.signedUrl : null
                })));
                setError(null);
            } else {
                setError(res.data?.message || 'Failed to fetch media');
            }
        } catch (error) {
            console.error('Error fetching media:', error);
            setError('An error occurred while fetching media');
        } finally {
            setFetchingMedia(false);
        }
    };

    useEffect(() => {
        if (selectedRow?.tireId) {
            fetchTireMedia();
        }
    }, [accountId, selectedRow?.tireId]);

    // Clear status messages after 2 seconds
    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    // Handle field updates - following the exact same pattern as fuel modal
    const handleFieldUpdate = async (value, fieldName) => {
        if (!selectedRow) return;

        setLoadingFields(prev => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const fieldMap = {
            vendor: 'vendorId',
            brand: 'brand',
            model: 'model',
            tin: 'tin',
            size: 'size',
            season: 'season',
            rim: 'rim',
            vehicle: 'vehicleId',
            position: 'position',
            status: 'status',
            note: 'note'
        };

        const payload = {};
        if (fieldName === 'vendor') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'vehicle') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'rim') {
            payload[fieldMap[fieldName]] = value;
        } else {
            payload[fieldMap[fieldName]] = value;
        }

        try {
            const response = await updateTire(accountId, selectedRow.tireId, payload);

            if (response.success) {
                const updatedRow = { ...selectedRow };
                switch (fieldName) {
                    case 'vendor':
                        updatedRow.vendor = {
                            vendorId: value,
                            name: vendorOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;
                    case 'vehicle':
                        updatedRow.vehicle = {
                            vehicleId: value,
                            name: vehicleOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;
                    case 'rim':
                        updatedRow.rim = value;
                        break;
                    default:
                        updatedRow[fieldName] = value;
                }

                setSelectedRow(updatedRow);
                setData(prevData => prevData.map(row =>
                    row.tireId === selectedRow.tireId ? updatedRow : row
                ));
                setUpdateStatus({
                    message: `${labelMap[fieldName] || fieldName} updated successfully!`,
                    type: 'success'
                });
                fetchTires?.();
            } else {
                setUpdateStatus({
                    message: response.data?.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error'
                });
            }
        } catch (error) {
            console.error('Error updating tire:', error);
            setUpdateStatus({
                message: `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error'
            });
        } finally {
            setLoadingFields(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    // Handle file upload
    const handleFileUpload = async (files) => {
        if (files.length === 0) return;

        setIsUploading(true);
        setUploadStatus({ message: '', type: '' });

        try {
            const uploadPromises = files.map((file) =>
                uploadMedia(accountId, file, 'tire', selectedRow.tireId)
            );
            const uploadResponses = await Promise.all(uploadPromises);

            const successfulUploads = uploadResponses.filter(res => res.success);
            const failedUploads = uploadResponses.filter(res => !res.success);

            if (failedUploads.length > 0) {
                setUploadStatus({
                    message: `${failedUploads.length} file(s) failed to upload`,
                    type: 'error',
                });
            }

            if (successfulUploads.length > 0) {
                const newFiles = successfulUploads.map(res => ({
                    mediaId: res.data.data.mediaId,
                    fileType: res.data.data.fileType,
                    fileName: res.data.data.fileName,
                    signedUrl: res.data.data.signedUrl,
                    signedThumbUrl: res.data.data.signedThumbUrl,
                    imagePreview: res.data.data.fileType?.startsWith('image/') ? res.data.data.signedThumbUrl || res.data.data.signedUrl : null
                }));

                setUploadedFiles(prev => [...prev, ...newFiles]);
                setUploadStatus({
                    message: `${successfulUploads.length} file(s) uploaded successfully!`,
                    type: 'success',
                });
            }
        } catch (error) {
            console.error('Error uploading media:', error);
            setUploadStatus({
                message: 'Failed to upload files',
                type: 'error',
            });
        } finally {
            setIsUploading(false);
        }
    };

    // Handle file removal
    const handleRemoveFile = async (fileToRemove) => {
        try {
            setUploadedFiles(prev => prev.filter(file => file.mediaId !== fileToRemove.mediaId));
        } catch (error) {
            console.error('Error removing file:', error);
            setUploadedFiles(prev => [...prev, fileToRemove]);
            setUploadStatus({
                message: 'Failed to delete file',
                type: 'error',
            });
        }
    };

    const labelMap = {
        vendor: 'Vendor',
        brand: 'Brand',
        model: 'Model',
        tin: 'TIN',
        size: 'Size',
        season: 'Season',
        rim: 'Rim',
        vehicle: 'Vehicle',
        position: 'Position',
        status: 'Status',
        note: 'Note'
    };

    const vendorOptions = prependDefault(vendors.map(v => ({ value: v.vendorId, label: v.label })), 'Vendor');
    const sizeOptions = prependDefault(sizes.map(v => ({ value: v.slug, label: v.label })), 'Size');
    const brandOptions = prependDefault(brands.map(v => ({ value: v.slug, label: v.label })), 'Brand');
    const vehicleOptions = prependDefault(vehicles.map(v => ({ value: v.vehicleId, label: v.label })), 'Vehicle');
    const seasonOptions = prependDefault(seasons.map(v => ({ value: v.slug, label: v.label })), 'Season');
    const rimOptions = [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
    ];

    useEffect(() => {
        fetchGlobalData();
    }, [])

    if (!selectedRow) return null;

    return (
        <div className="space-y-3 px-4 pt-3 relative">
            <ModalHeading className="text-left mb-3">Tire Detail Information</ModalHeading>

            <button
                className="hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full"
                onClick={() => setIsEditModalOpen(false)}
            >
                <RxCross2 className="text-xl" />
            </button>

            {(updateStatus.message || uploadStatus.message) && (
                <div className={`px-4 py-2 text-sm ${(updateStatus.type || uploadStatus.type) === 'success' ? 'text-green-600 bg-green-100' :
                    (updateStatus.type || uploadStatus.type) === 'error' ? 'text-red-600 bg-red-100' :
                        'text-blue-600 bg-blue-100'
                    } rounded-md mx-4`}>
                    {updateStatus.message || uploadStatus.message}
                </div>
            )}

            {(fetchingMedia || loadingGlobalData) ? (
                <Loader />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <EditableField
                        label="Vendor"
                        name="vendor"
                        type="select"
                        options={vendorOptions}
                        value={selectedRow.vendor?.vendorId || ''}
                        onChange={(value) => handleFieldUpdate(value, 'vendor')}
                        isLoading={loadingFields.vendor}
                    />

                    <EditableField
                        label="Brand"
                        name="brand"
                        type="select"
                        options={brandOptions}
                        value={selectedRow.brand || ''}
                        onChange={(value) => handleFieldUpdate(value, 'brand')}
                        isLoading={loadingFields.brand}
                    />

                    <EditableField
                        label="Model"
                        name="model"
                        value={selectedRow.model || ''}
                        onChange={(value) => handleFieldUpdate(value, 'model')}
                        type="text"
                        isLoading={loadingFields.model}
                    />

                    <EditableField
                        label="TIN"
                        name="tin"
                        value={selectedRow.tin || ''}
                        onChange={(value) => handleFieldUpdate(value, 'tin')}
                        type="text"
                        isLoading={loadingFields.tin}
                        maxLength={14}
                    />

                    <EditableField
                        label="Size"
                        name="size"
                        type="select"
                        options={sizeOptions}
                        value={selectedRow.size || ''}
                        onChange={(value) => handleFieldUpdate(value, 'size')}
                        isLoading={loadingFields.size}
                    />

                    <EditableField
                        label="Season"
                        name="season"
                        type="select"
                        options={seasonOptions}
                        value={selectedRow.season || ''}
                        onChange={(value) => handleFieldUpdate(value, 'season')}
                        isLoading={loadingFields.season}
                    />

                    <EditableField
                        label="Rim"
                        name="rim"
                        type="select"
                        options={rimOptions}
                        value={selectedRow.rim}
                        onChange={(value) => handleFieldUpdate(value, 'rim')}
                        isLoading={loadingFields.rim}
                    />

                    <EditableField
                        label="Vehicle"
                        name="vehicle"
                        type="select"
                        options={vehicleOptions}
                        value={selectedRow.vehicle?.vehicleId || ''}
                        onChange={(value) => handleFieldUpdate(value, 'vehicle')}
                        isEditable={false}
                        isLoading={loadingFields.vehicle}
                    />

                    <EditableField
                        label="Position"
                        name="position"
                        value={selectedRow.position || ''}
                        onChange={(value) => handleFieldUpdate(value, 'position')}
                        type="select"
                        isEditable={false}
                        isLoading={loadingFields.position}
                    />

                    <EditableField
                        label="Status"
                        name="status"
                        value={selectedRow.status || 'new'}
                        onChange={(value) => handleFieldUpdate(value, 'status')}
                        type="select"
                        isEditable={false}
                        isLoading={loadingFields.status}
                    />

                    <EditableField
                        label="Note"
                        name="note"
                        type="textarea"
                        rows={2}
                        value={selectedRow.note || ''}
                        onChange={(value) => handleFieldUpdate(value, 'note')}
                        isLoading={loadingFields.note}
                    />

                    <div className="sm:col-span-2">
                        <MultipleAttachmentInput
                            handleFileUpload={handleFileUpload}
                            uploadedFiles={uploadedFiles}
                            handleRemoveFile={handleRemoveFile}
                            isUploading={isUploading}
                            accountId={accountId}
                            fileInputRef={fileInputRef}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditModal;